import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';
import {
  ListResourcesDocument,
  ListResourcesQuery,
  ListResourcesQueryVariables,
  ResourceRowFragmentFragment,
  SortDirection,
  useBulkMoveResourcesMutation,
  useMoveAndRankResourcesMutation,
} from '@app/types/blackroom-api';
import { getResourceFilterFromScope } from '@app/shared/helpers/api';
import { ResourceScope } from '@app/types/blackroom-app';
import NEW_RESOURCE_FRAGMENT from '@features/Resource/api/newResourceFragment';
import { useParams } from 'react-router-dom';
import { PAGE_SIZE } from '@features/Resource/ResourceTable/types';

export default function useMoveResourceActions(
  scope: ResourceScope,
  requestVariables?: ListResourcesQueryVariables,
) {
  // Store
  const workspace = useWorkspace();
  const workspaceId = workspace.id;

  // API
  const [moveResourcesMutation] = useBulkMoveResourcesMutation();
  const [moveAndRankResourcesMutation] = useMoveAndRankResourcesMutation();

  // Routing
  const { folderId: parentId } = useParams();

  const moveResources = (
    targetResource: ResourceRowFragmentFragment,
    draggedResource: ResourceRowFragmentFragment,
  ) => {
    const resourcesToMove = [draggedResource];
    moveResourcesMutation({
      variables: {
        resourcesToMove: [draggedResource.id],
        to: targetResource.id,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        bulkMoveResources: resourcesToMove.map((r) => ({
          __typename: 'Resource',
          ...r,
          parentId: targetResource.id,
          updatedAt: new Date().toDateString(),
        })),
      },
      update(cache, { data }) {
        cache.evict({
          id: cache.identify({
            __typename: 'Resource',
            id: draggedResource.id,
          }),
          fieldName: 'path',
        });
        cache.gc();
        cache.modify({
          fields: {
            resources(getResourcesExistingRefs, { readField }) {
              const existingResourceRefs =
                getResourcesExistingRefs?.nodes ?? [];

              const targetPathCachedQueryData =
                cache.readQuery<ListResourcesQuery>({
                  query: ListResourcesDocument,
                  variables: {
                    filter: {
                      ...getResourceFilterFromScope(scope),
                      workspaceId,
                      parentId: targetResource.id,
                    },
                    sorter: {
                      createdAt: SortDirection.Desc,
                    },
                    limit: PAGE_SIZE,
                    offset: 0,
                  } as ListResourcesQueryVariables,
                });

              const queryResourceRefs = existingResourceRefs.filter(
                (ref: any) =>
                  !targetPathCachedQueryData?.resources.nodes?.some(
                    (n) => n.id === readField('id', ref),
                  ),
              );

              const resourcesToMoveRefs = resourcesToMove.map((r) => {
                const newResourceData = r;
                return cache.writeFragment({
                  data: newResourceData,
                  fragment: NEW_RESOURCE_FRAGMENT,
                });
              });

              return {
                ...getResourcesExistingRefs,
                pageInfo: {
                  ...queryResourceRefs.pageInfo,
                  totalCount: queryResourceRefs.length + resourcesToMove.length,
                },
                nodes: [
                  ...resourcesToMoveRefs,
                  ...(queryResourceRefs?.nodes ?? []),
                ],
              };
            },
          },
        });
      },
      refetchQueries: [
        {
          query: ListResourcesDocument,
          variables: {
            filter: {
              ...getResourceFilterFromScope(scope),
              workspaceId,
              parentId,
            },
            limit: PAGE_SIZE,
            offset: 0,
          } as ListResourcesQueryVariables,
        },
      ],
    });
  };

  const moveAndRankResources = (
    targetResource: ResourceRowFragmentFragment,
    draggedResource: ResourceRowFragmentFragment,
  ) =>
    moveAndRankResourcesMutation({
      variables: {
        workspaceId,
        sourceId: draggedResource.id,
        targetId: targetResource.id,
        parentId,
      },
      refetchQueries: [
        {
          query: ListResourcesDocument,
          variables:
            requestVariables ??
            ({
              filter: {
                parentId,
                workspaceId,
                ...getResourceFilterFromScope(scope),
              },
              limit: PAGE_SIZE,
              offset: 0,
              sorter: {
                rank: SortDirection.Asc,
              },
            } as ListResourcesQueryVariables),
        },
      ],
    });

  return { moveResources, moveAndRankResources };
}
